import {inject, injectable} from "inversify";
import type {IHttpClientService} from "data/services/http";
import type {AxiosResponse} from "axios";
import {Bindings} from "data/constants/bindings";
import type {
	IArticles,
	ICategories,
	ISections,
} from "data/stores/static_content/static_content.store";
import type {IPlayer} from "data/stores/players/players.store";
import type {ICountry} from "data/stores/countries/countries.store";
import type {IContest} from "data/stores/contests/contestsStore";
import type {ISquad} from "data/stores/squads/squads.store";
import type {IChecksums} from "data/stores/checksum/checksum.store";
import {Language} from "data/enums";
import {IContestStat} from "data/stores/contest_player_stats/contest_player_stats.store";
import {IPlayerStat} from "data/stores/player_stats/player_stats.store";
import {ITournament} from "data/stores/tournaments/tournaments.store";
import {IRound} from "data/types/rounds";

export interface IJSONProvider {
	countries(): Promise<AxiosResponse<ICountry[]>>;

	contests(): Promise<AxiosResponse<{items: IContest[]}>>;

	tournaments(): Promise<AxiosResponse<{items: ITournament[]}>>;

	players(): Promise<AxiosResponse<{items: IPlayer[]}>>;

	squads(): Promise<AxiosResponse<{items: ISquad[]}>>;

	checksums(): Promise<AxiosResponse<IChecksums>>;

	rounds(): Promise<AxiosResponse<{items: IRound[]}>>;

	player_stats(): Promise<AxiosResponse<{items: IPlayerStat[]}>>;

	contest_player_stats(contestId: number): Promise<AxiosResponse<{items: IContestStat[]}>>;

	translations(locale: string): Promise<AxiosResponse<Record<string, unknown>>>;

	helpCategories(locale: string): Promise<AxiosResponse<ICategories>>;

	helpSections(locale: string): Promise<AxiosResponse<ISections>>;

	helpArticles(locale: string): Promise<AxiosResponse<IArticles>>;
}

@injectable()
export class JSONProvider implements IJSONProvider {
	constructor(
		@inject(Bindings.JsonHTTPClient) private _jsonClient: IHttpClientService,
		@inject(Bindings.ContentJsonHTTPClient) private _contentJsonClient: IHttpClientService
	) {}

	countries = () => this._jsonClient.get<ICountry[]>("countries.json");
	contests = () => this._jsonClient.get<{items: IContest[]}>("contests.json");
	tournaments = () => this._jsonClient.get<{items: ITournament[]}>("tournaments.json");
	players = () => this._jsonClient.get<{items: IPlayer[]}>("players.json");
	squads = () => this._jsonClient.get<{items: ISquad[]}>("squads.json");
	rounds = () => this._jsonClient.get<{items: IRound[]}>("rounds.json");

	player_stats = () => this._jsonClient.get<{items: IPlayerStat[]}>("player_stats.json");
	contest_player_stats = (contestId: number) =>
		this._jsonClient.get<{items: IContestStat[]}>(`contest_player_stats/${contestId}.json`);
	checksums = () => this._jsonClient.get<IChecksums>("checksums.json");
	translations = (locale: string) =>
		this._jsonClient.get<Record<string, unknown>>(`loco/${locale}.json`);

	helpCategories = (locale: Language) =>
		this._contentJsonClient.get<ICategories>(`${locale}/categories.json`);
	helpSections = (locale: Language) =>
		this._contentJsonClient.get<ISections>(`${locale}/sections.json`);
	helpArticles = (locale: Language) =>
		this._contentJsonClient.get<IArticles>(`${locale}/articles.json`);
}
